import {
  faChevronDown,
  faInfoCircle,
  faSave
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MessageTemplateAutocomplete from 'components/common/MessageTemplateAutocomplete';
import MessageTemplateSelector from 'components/common/MessageTemplateSelector';
import Editor from 'components/editor/Editor';
import Loading from 'components/loading/Loading';
import useApi from 'helpers/api';
import {
  calculateEndTimeFromStartAndTimeSpent,
  calculateStartTimeFromEndAndTimeSpent,
  calculateTimeSpent
} from 'helpers/timeCalculations';
import { useAuthenticatedUser } from 'hooks/useAuthenticatedUser';
import useNotification from 'hooks/useNotification';
import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Col,
  Dropdown,
  Form,
  Modal,
  OverlayTrigger,
  Row,
  Tooltip
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import MaskedInput from 'react-text-mask';
import AppointmentsService from 'services/Appointments.service';
import PauseReasonsService from 'services/PauseReasons.service';

const TicketAppointmentRegistrationModal = ({
  showModal,
  handleClose,
  ticketId,
  refreshTicket,
  refreshList,
  refreshLogs
}) => {
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const api = useApi();
  const form = useRef(null);
  const navigate = useNavigate();
  const { authenticatedUser } = useAuthenticatedUser();

  const editorRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [validated, setValidated] = useState(false);
  const [pauseReasons, setPauseReasons] = useState([]);
  const [agents, setAgents] = useState([]);
  const [ticket, setTicket] = useState(null);
  const [filters] = useState({ includeInactives: false });

  const getTodayDatetime = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(today.getTime() - today.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 10); // Format as YYYY-MM-DD for date input
  };

  const initialState = {
    description: '',
    typeService: 'INTERNAL',
    timeSpent: '',
    startTime: '',
    endTime: '',
    date: getTodayDatetime(),
    pauseSla: false,
    pauseReason: { id: '' },
    user: { id: authenticatedUser?.id || '' },
    backdateReason: '',
    slaEligible: false
  };

  const [body, setBody] = useState(initialState);
  const isInitializedRef = useRef(false);
  const hasAutoFilledTimesRef = useRef(false);

  useEffect(() => {
    getPauseReasons();
    getAgents();
  }, []);

  useEffect(() => {
    if (showModal && !isInitializedRef.current) {
      clearFields();
      isInitializedRef.current = true;
      hasAutoFilledTimesRef.current = false;
    } else if (!showModal) {
      // Quando o modal fecha, resetar a flag para permitir inicialização na próxima abertura
      isInitializedRef.current = false;
      hasAutoFilledTimesRef.current = false;
    }
  }, [showModal, ticketId]);

  useEffect(() => {
    if (showModal && authenticatedUser) {
      setBody(prevBody => ({
        ...prevBody,
        user: { id: authenticatedUser?.id }
      }));
    }
  }, [showModal, authenticatedUser]);

  useEffect(() => {
    // Quando o modal abrir e a data estiver preenchida, preencher hora fim e calcular hora início
    if (
      showModal &&
      body.date &&
      !body.startTime &&
      !body.endTime &&
      !hasAutoFilledTimesRef.current
    ) {
      const now = new Date();
      const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(
        now.getMinutes()
      ).padStart(2, '0')}`;

      // Tempo gasto padrão: 01:00 (1 hora)
      const defaultTimeSpent = '01:00';

      // Colocar hora atual na hora INÍCIO
      const startTime = currentTime;

      // Calcular hora fim baseada na hora início e tempo gasto padrão
      const calculatedEnd = calculateEndTimeFromStartAndTimeSpent(
        startTime,
        defaultTimeSpent
      );

      if (calculatedEnd) {
        hasAutoFilledTimesRef.current = true;
        setBody(prevBody => ({
          ...prevBody,
          endTime: calculatedEnd,
          startTime: startTime,
          timeSpent: defaultTimeSpent
        }));
      }
    }
  }, [showModal, body.date, body.startTime, body.endTime]);

  const getPauseReasons = async () => {
    const queryParams = new URLSearchParams(filters).toString();
    const response = await PauseReasonsService.list(queryParams);

    response.content && setPauseReasons(response.content);
  };

  const getAgents = async () => {
    try {
      const queryParams = new URLSearchParams({
        page: 0,
        size: 1000,
        inactive: false
      }).toString();
      const response = await api.agents.list(queryParams);
      if (response.content) {
        setAgents(response.content);
      }
    } catch (error) {
      console.error('Erro ao buscar agentes:', error);
    }
  };

  const isDescriptionEmpty =
    body.description.trim() === '' || body.description === '<p><br></p>';

  const isBackdated = () => {
    if (!body.date || !body.startTime) return false;

    // Tolerância em minutos antes de considerar como retroativo
    const TOLERANCE_MINUTES = 10;

    const now = new Date();
    // Subtrair a tolerância da hora atual
    const adjustedTime = new Date(now.getTime() - TOLERANCE_MINUTES * 60000);

    const today = new Date(
      adjustedTime.getTime() - adjustedTime.getTimezoneOffset() * 60000
    )
      .toISOString()
      .slice(0, 10);
    const currentTime = `${String(adjustedTime.getHours()).padStart(
      2,
      '0'
    )}:${String(adjustedTime.getMinutes()).padStart(2, '0')}`;

    if (body.date < today) return true;
    if (body.date === today && body.startTime < currentTime) return true;
    return false;
  };

  const isBackdateReasonEmpty =
    isBackdated() &&
    (body.backdateReason.trim() === '' ||
      body.backdateReason === '<p><br></p>');

  const validateForm = e => {
    if (
      form.current.checkValidity() === false ||
      body.timeSpent === '' ||
      body.startTime === '' ||
      body.endTime === '' ||
      isDescriptionEmpty ||
      isBackdateReasonEmpty
    ) {
      e.preventDefault();
      e.stopPropagation();
      setValidated(true);
      return false;
    }
    return true;
  };

  const prepareBody = () => {
    const bodyToSend = { ...body };
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    bodyToSend.date = `${bodyToSend.date}T${hours}:${minutes}`;

    // Garantir que user seja sempre um objeto { id: number }
    if (bodyToSend.user && typeof bodyToSend.user === 'number') {
      bodyToSend.user = { id: bodyToSend.user };
    } else if (bodyToSend.user && !bodyToSend.user.id && bodyToSend.user) {
      bodyToSend.user = { id: bodyToSend.user };
    }

    return bodyToSend;
  };

  const handleApiResponse = response => {
    if (response.id) {
      showSuccessNotification('Apontamento criado com sucesso');
      refreshTicket();
      refreshList();
      refreshLogs();
      handleClose();
    } else if (response.status === 403) {
      showErrorNotification(
        'Você não tem permissão para realizar esta operação'
      );
    } else {
      showErrorNotification(
        response.userMessage || 'Erro ao realizar operação'
      );
    }

    setIsLoading(false);
  };

  const create = async e => {
    if (!validateForm(e)) return;
    setIsLoading(true);
    const bodyToSend = prepareBody();
    const response = await AppointmentsService.create(ticketId, bodyToSend);
    handleApiResponse(response);
  };

  const createAndPause = async e => {
    if (!validateForm(e)) return;
    setIsLoading(true);
    const bodyToSend = prepareBody();
    const response = await AppointmentsService.createAndPause(
      ticketId,
      bodyToSend
    );

    handleApiResponse(response);
  };

  const clearFields = () => {
    setBody(initialState);
  };

  const getCurrentDate = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(today.getTime() - today.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 10); // Format as YYYY-MM-DD for date input max
  };

  const today = getCurrentDate();

  const getTimeSpentStyle = () => {
    if (!(body.startTime && body.endTime)) return {};

    const isDark =
      document.documentElement.getAttribute('data-bs-theme') === 'dark';

    if (isDark) {
      return {
        backgroundColor: 'rgba(0, 210, 122, 0.2)',
        borderColor: 'rgba(0, 210, 122, 0.4)',
        color: '#00d27a'
      };
    }

    return {
      backgroundColor: '#d4edda',
      borderColor: '#c3e6cb'
    };
  };

  const setFieldBody = (fieldName, value) => {
    setBody(prevBody => {
      const newBody = { ...prevBody };

      // Tratar campo user separadamente - sempre manter como objeto { id: number }
      if (fieldName === 'user') {
        newBody.user = { id: value };
      } else {
        newBody[fieldName] = value;
      }

      // Se startTime ou endTime foram alterados, calcular timeSpent quando ambos estiverem preenchidos
      if (fieldName === 'startTime' || fieldName === 'endTime') {
        const start = fieldName === 'startTime' ? value : newBody.startTime;
        const end = fieldName === 'endTime' ? value : newBody.endTime;

        if (start && end) {
          const calculatedTime = calculateTimeSpent(start, end);
          if (calculatedTime) {
            newBody.timeSpent = calculatedTime;
          }
        }
      }

      // Se timeSpent foi alterado, calcular baseado nos campos disponíveis
      if (fieldName === 'timeSpent') {
        const timeSpent = value;

        // Validar formato HH:MM
        if (timeSpent && timeSpent.match(/^\d{2}:\d{2}$/)) {
          const start = newBody.startTime;
          const end = newBody.endTime;

          // Se temos início e tempo gasto, calcular fim
          if (start && !end) {
            const calculatedEnd = calculateEndTimeFromStartAndTimeSpent(
              start,
              timeSpent
            );
            if (calculatedEnd) {
              newBody.endTime = calculatedEnd;
            }
          }
          // Se temos fim e tempo gasto, calcular início
          else if (end && !start) {
            const calculatedStart = calculateStartTimeFromEndAndTimeSpent(
              end,
              timeSpent
            );
            if (calculatedStart) {
              newBody.startTime = calculatedStart;
            }
          }
          // Se temos início e fim, recalcular hora fim baseada no início e no novo tempo gasto
          else if (start && end) {
            const calculatedEnd = calculateEndTimeFromStartAndTimeSpent(
              start,
              timeSpent
            );
            if (calculatedEnd) {
              newBody.endTime = calculatedEnd;
            }
          }
        }
      }

      return newBody;
    });
  };

  return (
    <Modal size="lg" show={showModal} onHide={handleClose}>
      {isLoading && <Loading />}
      <Modal.Header closeButton>
        <Modal.Title>Novo apontamento</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form validated={validated} ref={form}>
          <Row className="mb-3">
            <Form.Group as={Col} md="6" controlId="appointment.type">
              <Form.Label>
                Tipo de Apontamento <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                aria-label="Tipo"
                value={body.typeService}
                onChange={e => setFieldBody('typeService', e.target.value)}
                required
              >
                <option value="INTERNAL" key="INTERNAL">
                  Interno
                </option>
                <option value="EXTERNAL" key="EXTERNAL">
                  Externo
                </option>
              </Form.Select>
              <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                Escolha uma opção!
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group as={Col} md="6" controlId="appointment.agent">
              <Form.Label>
                Agente <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                aria-label="Agente"
                value={body.user.id}
                onChange={e => setFieldBody('user', Number(e.target.value))}
                required
              >
                {agents.map(agent => (
                  <option key={agent.id} value={agent.id}>
                    {agent.firstName} {agent.lastName}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} md="3" controlId="appointment.date">
              <Form.Label>
                Data <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="date"
                value={body.date}
                onChange={e => setFieldBody('date', e.target.value)}
                required
                max={today}
              />
              <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                Selecione uma data!
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group as={Col} md="3" controlId="appointment.startTime">
              <Form.Label>
                Hora Início <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="time"
                required
                value={body.startTime}
                onChange={e => setFieldBody('startTime', e.target.value)}
              />
              <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
            </Form.Group>

            <Form.Group as={Col} md="3" controlId="appointment.endTime">
              <Form.Label>
                Hora Fim <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="time"
                required
                value={body.endTime}
                onChange={e => setFieldBody('endTime', e.target.value)}
              />
              <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
            </Form.Group>

            <Form.Group as={Col} md="3" controlId="appointment.timeSpent">
              <Form.Label>
                Tempo <span className="text-danger">*</span>
              </Form.Label>
              <MaskedInput
                mask={[/[0-9]/, /\d/, ':', /\d/, /\d/]}
                placeholder="HH:MM"
                required
                guide={false}
                value={body.timeSpent}
                onChange={e => setFieldBody('timeSpent', e.target.value)}
                style={getTimeSpentStyle ? getTimeSpentStyle() : {}}
                render={(ref, props) => <Form.Control ref={ref} {...props} />}
              />
              <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                Insira o tempo trabalhado!
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} md={2}>
              <Form.Label>Pausar SLA</Form.Label>
              <Form.Switch
                value={body.pauseSla}
                onChange={() => setFieldBody('pauseSla', !body.pauseSla)}
              />
            </Form.Group>

            <Form.Group as={Col} md={3}>
              <Form.Label>
                Regularizar SLA{' '}
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id="sla-tooltip">
                      Ao habilitar esta opção, se o tempo informado estiver
                      dentro do prazo de resposta, o SLA será marcado como
                      cumprido.
                    </Tooltip>
                  }
                >
                  <span>
                    <FontAwesomeIcon
                      icon={faInfoCircle}
                      className="text-secondary cursor-pointer ms-1"
                    />
                  </span>
                </OverlayTrigger>
              </Form.Label>
              <Form.Switch
                value={body.slaEligible}
                checked={body.slaEligible}
                onChange={() => setFieldBody('slaEligible', !body.slaEligible)}
              />
            </Form.Group>

            {body.pauseSla && (
              <Form.Group as={Col} md={4}>
                <Form.Label>
                  Motivo de pausa <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  required={body.pauseReason}
                  value={body.pauseReason.id}
                  onChange={e =>
                    setBody({ ...body, pauseReason: { id: e.target.value } })
                  }
                >
                  <option value="">Selecione</option>
                  {pauseReasons.map((item, i) => (
                    <option value={item.id} key={i}>
                      {item.name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
                <Form.Control.Feedback type="invalid">
                  Motivo de pausa obrigatório.
                </Form.Control.Feedback>
              </Form.Group>
            )}
          </Row>

          <Form.Group controlId="appointment.profile">
            {isBackdated() && (
              <Form.Group
                className="mb-3"
                controlId="appointment.backdateReason"
              >
                <div
                  className="alert alert-warning py-2 mb-2 fs-10"
                  role="alert"
                >
                  <strong>Atenção:</strong> O horário do apontamento é
                  retroativo. Por favor, forneça uma justificativa.
                </div>
                <Form.Label>
                  Justificativa <span className="text-danger">*</span>
                </Form.Label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={body.backdateReason}
                  onChange={e => setFieldBody('backdateReason', e.target.value)}
                  required={isBackdated()}
                />
                {validated && isBackdateReasonEmpty && (
                  <span className="d-block text-danger fs-10 mt-1">
                    Justificativa é obrigatória para apontamentos retroativos.
                  </span>
                )}
              </Form.Group>
            )}

            <Form.Label className="d-flex align-items-center justify-content-between">
              <span>
                Descrição
                <span className="text-danger"> *</span>
              </span>
              <MessageTemplateSelector
                type="APPOINTMENT"
                ticketId={ticketId}
                editorRef={editorRef}
                onTemplateApplied={content => {
                  setFieldBody('description', content);
                }}
              />
            </Form.Label>

            <Editor
              data={body.description}
              ref={editorRef}
              value={value => setFieldBody('description', value)}
            />
            <MessageTemplateAutocomplete
              editorRef={editorRef}
              type="APPOINTMENT"
              ticketId={ticketId}
              onTemplateApplied={content => {
                setFieldBody('description', content);
              }}
            />
            <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
            {validated && isDescriptionEmpty && (
              <span className="d-block text-danger fs-10 mt-1">
                Campo obrigatório.
              </span>
            )}
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="falcon-default" onClick={handleClose}>
          Fechar
        </Button>
        <div className="position-relative d-flex">
          <Button
            style={{ borderRadius: '40px 0 0 40px' }}
            variant="primary"
            type="submit"
            className="m-0"
            onClick={create}
          >
            <FontAwesomeIcon icon={faSave} className="me-2" />
            Salvar
          </Button>
          <Dropdown drop="up" className="d-flex m-0" style={{ height: '38px' }}>
            <Dropdown.Toggle as={CustomToggle}></Dropdown.Toggle>

            <Dropdown.Menu
              className="position-absolute w-100"
              style={{ transform: 'translateX(-65%)' }}
            >
              <Dropdown.Item onClick={createAndPause}>
                Salvar e Pausar
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

const CustomToggle = React.forwardRef(({ onClick }, ref) => (
  <Button
    style={{
      borderRadius: '0 5px 5px 0',
      height: '38px',
      padding: '6px 12px'
    }}
    ref={ref}
    className="m-0 d-flex align-items-center justify-content-center"
    onClick={e => {
      e.preventDefault();
      onClick(e);
    }}
  >
    <FontAwesomeIcon icon={faChevronDown} />
  </Button>
));

export default TicketAppointmentRegistrationModal;
